import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["date", "start", "end", "startsAt", "endsAt", "summary", "error"]
  static values = { invalidMessage: String, nextDayLabel: String }

  connect() {
    this.durationMinutes = this.duration()
    this.sync()
  }

  dateChanged() {
    this.sync()
  }

  startChanged() {
    if (this.durationMinutes > 0) this.setEndFromDuration(this.durationMinutes)
    this.sync()
  }

  endChanged() {
    this.durationMinutes = this.duration()
    this.sync()
  }

  setDuration(event) {
    const minutes = Number(event.params.minutes)
    if (!Number.isFinite(minutes) || minutes <= 0) return

    this.durationMinutes = minutes
    this.setEndFromDuration(minutes)
    this.sync()

    this.element.querySelectorAll("[data-reservation-time-minutes-param]").forEach((button) => {
      const active = Number(button.dataset.reservationTimeMinutesParam) === minutes
      button.classList.toggle("btn-secondary", active)
      button.classList.toggle("btn-outline-secondary", !active)
    })
  }

  validate(event) {
    this.sync()
    if (this.valid()) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.showError()
    this.endTarget.focus()
  }

  sync() {
    if (this.dateTarget.value && this.startTarget.value) {
      this.startsAtTarget.value = `${this.dateTarget.value}T${this.startTarget.value}`
    }
    if (this.dateTarget.value && this.endTarget.value) {
      const endDate = this.crossesMidnight() ? this.nextDate(this.dateTarget.value) : this.dateTarget.value
      this.endsAtTarget.value = `${endDate}T${this.endTarget.value}`
    }

    if (this.valid()) {
      this.durationMinutes = this.duration()
      this.hideError()
      const nextDay = this.crossesMidnight() ? ` · ${this.nextDayLabelValue}` : ""
      this.summaryTarget.textContent = `${this.formatDuration(this.durationMinutes)}${nextDay}`
    } else {
      this.summaryTarget.textContent = ""
    }
  }

  setEndFromDuration(minutes) {
    const startMinutes = this.minutesFromTime(this.startTarget.value)
    if (startMinutes === null) return

    const endMinutes = (startMinutes + minutes) % (24 * 60)
    const endTime = this.timeFromMinutes(endMinutes)

    if (this.endTarget._flatpickr) {
      this.endTarget._flatpickr.setDate(endTime, true, "H:i")
    } else {
      this.endTarget.value = endTime
    }
  }

  valid() {
    const startMinutes = this.minutesFromTime(this.startTarget.value)
    const endMinutes = this.minutesFromTime(this.endTarget.value)
    return this.dateTarget.value !== "" && startMinutes !== null && endMinutes !== null && endMinutes !== startMinutes
  }

  duration() {
    const startMinutes = this.minutesFromTime(this.startTarget.value)
    const endMinutes = this.minutesFromTime(this.endTarget.value)
    if (startMinutes === null || endMinutes === null) return 0

    const difference = endMinutes - startMinutes
    return difference > 0 ? difference : difference + (24 * 60)
  }

  crossesMidnight() {
    const startMinutes = this.minutesFromTime(this.startTarget.value)
    const endMinutes = this.minutesFromTime(this.endTarget.value)
    return startMinutes !== null && endMinutes !== null && endMinutes < startMinutes
  }

  nextDate(value) {
    const [year, month, day] = value.split("-").map(Number)
    const date = new Date(Date.UTC(year, month - 1, day + 1))
    return date.toISOString().slice(0, 10)
  }

  minutesFromTime(value) {
    if (!/^\d{2}:\d{2}$/.test(value || "")) return null
    const [hours, minutes] = value.split(":").map(Number)
    return (hours * 60) + minutes
  }

  timeFromMinutes(value) {
    const hours = Math.floor(value / 60).toString().padStart(2, "0")
    const minutes = (value % 60).toString().padStart(2, "0")
    return `${hours}:${minutes}`
  }

  formatDuration(minutes) {
    const hours = Math.floor(minutes / 60)
    const remainder = minutes % 60
    return [hours > 0 ? `${hours} h` : null, remainder > 0 ? `${remainder} min` : null].filter(Boolean).join(" ")
  }

  showError() {
    this.errorTarget.textContent = this.invalidMessageValue
    this.errorTarget.classList.remove("d-none")
    this.visibleEndInput().classList.add("is-invalid")
  }

  hideError() {
    this.errorTarget.classList.add("d-none")
    this.visibleEndInput().classList.remove("is-invalid")
  }

  visibleEndInput() {
    return this.endTarget._flatpickr?.altInput || this.endTarget
  }
}
